import { Component, ViewChild, ElementRef, Output, EventEmitter, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { IonButton, Platform } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import {
    AuthService,
    LoaderService,
    LocalStorageService
} from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import { LoginCardComponent } from '@shared/components';
import type { TLoginCredentials } from '@shared/models';

type TLoginState = {
    loginText: string;
    version: string;
};

@Component({
    'selector': 'app-login',
    'templateUrl': './login.page.html',
    'styleUrls': ['./login.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, LoginCardComponent ]
})
export class LoginPage extends ViewBasePage implements OnInit, OnDestroy {
    @ViewChild('password', { read: ElementRef }) public passwordRef: ElementRef;
    @ViewChild('loginButton') private loginButton: IonButton;
    @Output() public userCredentialEvent = new EventEmitter<TLoginCredentials>();
    private passwordVisible = false;
    public splash = true;
    // private onDevice: boolean;
    public backend = 'backend';
    public user: TLoginCredentials = { 'email': '', 'userUid': '' };
    public vm: TLoginState = {
        loginText: '',
        version: ''
    };
    public showLoginDiv = true;

    constructor( private authService: AuthService, private elementRef: ElementRef,
        private loaderService: LoaderService, private platform: Platform,
        private localStorageService: LocalStorageService, injector: Injector) {
        super(injector);
    }

    public override async ngOnInit() {
        try {
            super.ngOnInit();

            // If running on a device, then remove the splash screen after 10 seconds.
            // Otherwise, remove the splash immediately.
            // ptb  const onDevice = this.platform.is('cordova');
            // const removeSplashMillisecs = (onDevice ? 10000 : 0);
            // setTimeout(() => {
            //     this.splash = false;
            // }, removeSplashMillisecs);

            // The visibility of the sidemenu is dependent on whether the user is logged in.
            // So, to ensure the sidemenu is not visible, logout.
            //        this.authService.logout();

            this.vm.version = Constants.BACKEND_VERSION;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    public override async ngOnDestroy() {
        super.ngOnDestroy();
    }

    public async handleUserCredentials(cred: any): Promise<void> {
        // console.log('handleUserCredentials, User Credentials:', cred);

        try {
            await this.uxHelperService.showSpinner('Logging you in.');
            // const cred: TLoginCredentials = await this.authService.login(email, password);
            await this.loaderService.prepareSession(cred.userUid);

            const user = this.sessionService.getCurrentUser();
            if (user.role.toLowerCase() !== 'manager' || !user.email.toLowerCase().includes('shuttlenomix')) {
                await this.uxHelperService.showAlert('Error', 'This user is not allowed access to the backend.');
                return;
            }

            this.router.navigateByUrl('accounts');
            this.eventsService.publish(Constants.EVENT_LOGIN);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public onEyeClick() {
        this.passwordVisible = !this.passwordVisible;
        if (this.passwordVisible) {
            this.passwordRef.nativeElement.setAttribute('type', 'text');
        } else {
            this.passwordRef.nativeElement.setAttribute('type', 'password');
        }
    }

    // private showInternetConnectionState(isConnected: boolean) {
    //     this.vm.loginText = isConnected ? 'Login' : 'No Internet Available';
    //     this.loginButton.disabled = !isConnected;
    // }

    public onToggleLoginVisibility() {
        this.showLoginDiv = !this.showLoginDiv;
        return this.showLoginDiv;
    }

    public async onLoginClick() {
        // this.restoreUserOrLogin();

        const email: string = this.user.email;
        const password: string = this.user.password;

        if (!email || !password) {
            await this.uxHelperService.showAlert('Error', 'Please enter both email and password');
            return;
        }

        try {
            await this.uxHelperService.showSpinner('Logging you in.');
            const cred: TLoginCredentials = await this.authService.login(email, password);
            await this.loaderService.prepareSession(cred.userUid);

            const user = this.sessionService.getCurrentUser();
            if (user.role.toLowerCase() !== 'manager' || !user.email.toLowerCase().includes('shuttlenomix')) {
                await this.uxHelperService.showAlert('Error', 'This user is not allowed access to the backend.');
                return;
            }

            this.router.navigateByUrl('accounts');
            this.eventsService.publish(Constants.EVENT_LOGIN);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public async onResetPasswordClick() {
        if (!this.user.email) {
            await this.uxHelperService.showAlert('Error', 'Your email address is required.');
            return;
        }

        this.uxHelperService.showSpinner('Sending email.');
        try {
            const success = await this.authService.sendPasswordResetEmail(this.user.email);
            if (success) {
                await this.uxHelperService.showAlert('Email Successfully Sent',
                    'Please follow the instructions in the email to reset your password.');
            } else {
                await this.uxHelperService.showAlert('Email Error',
                    'Please verify the spelling of the email address and your internet connection.');
            }
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    // private restoreUserOrLoginAtiqsVersion() {
    //     this.authService.isAuthenticatedNew().then((_loggedin) => {
    //         if(_loggedin){
    //             const user = this.authService.getCurrentUser();
    //             this.loaderService.prepareAppForUser(user.uid).then(()=>{
    //                 this.eventsService.publish(Constants.EVENT_LOGIN);
    //                 this.router.navigateByUrl('/dashboard');
    //             });
    //         } else {
    //             this.router.navigateByUrl('/login');
    //         }
    //     });   
    // }


    // This works!
    private async restoreUserOrLogin() {
        await this.uxHelperService.showSpinner('Logging you back in.');
        const user = this.authService.getCurrentUser();
        if (user) {
            await this.loaderService.prepareSession(user.uid);

            // If the account has been cancelled, log the user out and return immediately.
            if (this.sessionService.isAccountCancelled()) {
                await this.authService.logout();
                await this.uxHelperService.showAlert('Information', 'This account has been cancelled.');
                return;
            }

            // 
            this.eventsService.publish(Constants.EVENT_LOGIN);
            this.router.navigateByUrl('/dashboard');
        } else {
            this.router.navigateByUrl('/login');
        }
        await this.uxHelperService.hideSpinner();
    }

    private async restoreUser() {
        try {
            await this.uxHelperService.showSpinner('Logging you in.');
            const user = this.authService.getCurrentUser();
            if (user) {
                await this.loaderService.prepareSession(user.uid);

                // If the account has been cancelled, log the user out and return immediately.
                if (this.sessionService.isAccountCancelled()) {
                    await this.authService.logout();
                    await this.uxHelperService.showAlert('Information', 'This account has been cancelled.');
                    return;
                }

                // 
                this.eventsService.publish(Constants.EVENT_LOGIN);
                this.router.navigateByUrl('/dashboard');
            }
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }
}
